// panelPosition — where the floating Talk to support panel sits, as pure maths,
// plus a remembered spot (per browser, a convenience only).
//
// The caller drags the panel by its top bar so it never covers the button the
// agent is pointing at. The panel must always stay fully reachable: every
// position is clamped inside the window with a margin, on drag, on load and on
// resize. Below DRAG_MIN_WIDTH (phones) it stays docked along the bottom.

export interface Point { x: number; y: number }
export interface Size { w: number; h: number }

const PANEL_MARGIN = 12
export const DRAG_MIN_WIDTH = 640
const STORE_KEY = 'kf49.supportPanel.pos'

/** Keep the panel's top-left so the whole panel is inside the window. */
export function clampPosition(p: Point, panel: Size, view: Size, margin = PANEL_MARGIN): Point {
  const maxX = Math.max(margin, view.w - panel.w - margin)
  const maxY = Math.max(margin, view.h - panel.h - margin)
  return {
    x: Math.round(Math.min(Math.max(p.x, margin), maxX)),
    y: Math.round(Math.min(Math.max(p.y, margin), maxY)),
  }
}

/** Bottom-right, where the panel opens the first time. */
export function defaultPosition(panel: Size, view: Size, margin = 16): Point {
  return clampPosition({ x: view.w - panel.w - margin, y: view.h - panel.h - margin }, panel, view)
}

/** One keyboard nudge (arrow keys on the move handle). */
export function nudge(p: Point, key: string, step = 24): Point | null {
  const d: Record<string, Point> = { ArrowLeft: { x: -step, y: 0 }, ArrowRight: { x: step, y: 0 }, ArrowUp: { x: 0, y: -step }, ArrowDown: { x: 0, y: step } }
  const m = d[key]
  return m ? { x: p.x + m.x, y: p.y + m.y } : null
}

export function loadPosition(): Point | null {
  try {
    const raw = window.localStorage.getItem(STORE_KEY)
    const v = raw ? JSON.parse(raw) as Partial<Point> : null
    return v && Number.isFinite(v.x) && Number.isFinite(v.y) ? { x: v.x as number, y: v.y as number } : null
  } catch {
    return null
  }
}

export function savePosition(p: Point | null): void {
  try {
    if (p) window.localStorage.setItem(STORE_KEY, JSON.stringify(p))
    else window.localStorage.removeItem(STORE_KEY)
  } catch { /* private mode or blocked storage: the panel just opens bottom-right */ }
}
